import {
  ACCEPTED_FILE_TYPES,
  CHAT_INPUT_ACTIONS,
  compareErrorCode,
  CONVIMAGE_LEGACYMODE,
  IMAGE_INPUT_ID,
  toggleOverlayLogin,
  useAppDispatch,
  useTranslation,
  useUserInfo,
} from "@next-platform/client-common-core";
import { SubCapabilityKey, useApplicationCapabilities } from "@next-platform/client-common-core/infra/application/capabilities";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import {
  ButtonIcon,
  ButtonSize,
  ButtonVariant,
  DropDownMenuItem,
  IconVariants,
  SpinnerSize,
  Tooltip,
  useMedia,
} from "@next-platform/client-common-spicy-ui";
import { RateLimitingErrorType } from "@next-platform/packages-ts-types";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import { Col, Row } from "@/components";
import PermissionsModal from "@/components/common/PermissionsModal";
import { CHAT_PAGE_WIDTH, SPICYCHAT_USER_PERMISSIONS } from "@/constants";
import { ModalType } from "@/pages/Chat/components/ChatBubble/types";
import CommandInputBox from "@/pages/Chat/components/CommandInputBox";
import { CHAT_MESSAGE_MAX_LENGTH } from "@/pages/Chat/components/constants";
import ImageUploadPreview from "@/pages/Chat/components/ImageUploadPreview";
import { ModalPremiumAccess } from "@/pages/Chat/components/ModalPremiumAccess";
import ModalPremiumTraining from "@/pages/Chat/components/ModalPremiumTraining";
import ModalTraining from "@/pages/Chat/components/ModalTraining";
import useChatDraft from "@/pages/Chat/hooks/useChatDraft";
import useChatImageUpload from "@/pages/Chat/hooks/useChatImageUpload";
import { SpeechRecognition } from "@/pages/ChatStory/components/StoryChatInput/types";
import { cn, getIsPartner, handleSpeechRecognitionError } from "@/utils";
import { typedLocalStorageService } from "@/utils/localStorageRe";

import InputActionMenu from "./components/InputActionMenu";
import { ChatInputProps } from "./types";

const logger = appLogger.withTag("ChatInput");
const WIDE_SCREEN_WIDTH_THRESHOLD = 1080;

function ChatInput({
  character,
  conversationId,
  typingMode,
  isCloneLoading,
  errorCode,
  scrollToBottom,
  isInputActionDisabled,
  isLastMessageImage,
  wrapperRef,
  generate,
  setIsInputActionDisabled,
  isMessageEditMode,
  setCharacterThatIsTyping,
  isAutoResponseActivated,
  onPendingMessage,
  hasPendingMessage,
  isCharacterAvatarBlurred,
  isChatWithGroup,
  hideSuggest = false,
  droppedFile,
  shouldRenderImageUploader,
  setDroppedFile,
  onInputFocusChange,
  isGroupBelowMinimum,
}: ChatInputProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { isDesktop, isMobile } = useMedia();
  const { loadDraft, saveDraft, clearDraft } = useChatDraft({ characterId: character?.id, conversationId });
  const [message, setMessage] = useState(() => loadDraft());
  const refIsSending = useRef(false);
  const refSentFromNewChat = useRef(false);
  const refPendingDraftText = useRef<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/naming-convention -- External API response uses snake_case property
  const characterWithLora = character as unknown as { lora_status?: string };
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const { permissions, isAuthenticated } = useUserInfo();
  const imageGenerationCapability = useApplicationCapabilities(SubCapabilityKey.ChatImageImageGeneration);
  const isImageGenerationAllowed = imageGenerationCapability?.allowed ?? false;
  const isImageGenerationPaywall = imageGenerationCapability?.isPaywall ?? false;
  const [isAutoPilotPressed, setIsAutoPilotPressed] = useState(false);
  const [isPermissionsModal, setIsPermissionsModal] = useState(false);
  const [isMicrophoneDenied, setIsMicrophoneDenied] = useState(false);
  const [openModalType, setOpenModalType] = useState<ModalType | null>(null);
  const shouldShowLoginOverlay = !isAuthenticated && character?.is_nsfw;

  const [attachedImageKeyToSend, setAttachedImageKeyToSend] = useState<string | null>(null);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [uploaderResetSignal, setUploaderResetSignal] = useState(0);
  const [oocCommandMessage, setOocCommandMessage] = useState("");
  const [searchCommandMessage, setSearchCommandMessage] = useState("");
  const [isNormalCommandBoxVisible, setIsNormalCommandBoxVisible] = useState(false);
  const [isSearchCommandBoxVisible, setIsSearchCommandBoxVisible] = useState(false);

  const messageToSend =
    (searchCommandMessage?.trim() ? `/search ${searchCommandMessage}\n` : "") +
    (oocCommandMessage?.trim() ? `/cmd ${oocCommandMessage}\n` : "") +
    message;
  const isPartner = getIsPartner();

  const isChatLimitBreached = compareErrorCode(errorCode, RateLimitingErrorType.RATE_LIMIT_BREACHED);
  const chatAbortRef = useRef(new AbortController());
  const chatAbortController = chatAbortRef.current;
  const isSubmittingRef = useRef(false);
  const messageHasSomeValue = messageToSend.trim().length > 0 || attachedImageKeyToSend;
  const isDisabled = !!(typingMode || isCloneLoading || isChatLimitBreached || hasPendingMessage || isGroupBelowMinimum);
  const isImageGenerationVisible = isImageGenerationAllowed || isImageGenerationPaywall;
  const isImageIconHidden = messageToSend || isInputActionDisabled || messageHasSomeValue || isPartner || !isImageGenerationVisible;
  const [isActionMenuAtRight, setIsActionMenuAtRight] = useState(false);

  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const {
    isUploading,
    displayImageKey,
    handleClearImage,
    processDroppedFile,
    handleUploadClick,
    isImageCleared,
    imageId,
    restoreImage,
    getAttachedImageDataUrl,
  } = useChatImageUpload({
    fileInputRef,
    onImageUploaded: (key: string) => {
      setAttachedImageKeyToSend?.(key);
      setIsImageUploading?.(false);
    },
    onUploadingChange: setIsImageUploading,
    resetSignal: uploaderResetSignal,
    disabled: isDisabled,
    isEnabled: shouldRenderImageUploader,
    onImageCleared: () => {
      setAttachedImageKeyToSend(null);
      setIsImageModalOpen(false);
    },
  });

  useEffect(() => {
    if (droppedFile) {
      if (!isAuthenticated) {
        dispatch(toggleOverlayLogin());
        setDroppedFile(null);
        return;
      }
      processDroppedFile([droppedFile]);
      setDroppedFile(null);
    }
  }, [droppedFile, isAuthenticated, dispatch, processDroppedFile, setDroppedFile]);

  const refSaveDraft = useRef(saveDraft);
  refSaveDraft.current = saveDraft;

  // Persist the composer text as a per-conversation draft (skipped while a send
  // is in flight so the optimistic clear does not wipe the retained draft).
  useEffect(() => {
    if (refIsSending.current) {
      return;
    }
    const pendingDraftText = refPendingDraftText.current;
    refPendingDraftText.current = null;
    if (pendingDraftText !== null && message.length === 0) {
      refSaveDraft.current(pendingDraftText);
      return;
    }
    refSaveDraft.current(message);
  }, [message]);

  const refPreviousConversationId = useRef(conversationId);
  useEffect(() => {
    const previousConversationId = refPreviousConversationId.current;
    refPreviousConversationId.current = conversationId;
    refPendingDraftText.current = null;
    const wasIdAssignedByOwnSend = refSentFromNewChat.current;
    refSentFromNewChat.current = false;
    if (!previousConversationId && conversationId && wasIdAssignedByOwnSend) {
      return;
    }
    setMessage(loadDraft());
  }, [conversationId, loadDraft]);

  // Drafts are not carried across a guest -> logged-in transition.
  const refWasAuthenticated = useRef(isAuthenticated);
  useEffect(() => {
    if (!refWasAuthenticated.current && isAuthenticated) {
      clearDraft();
      setMessage("");
    }
    refWasAuthenticated.current = isAuthenticated;
  }, [isAuthenticated, clearDraft]);

  // On mount, place the caret at the end of a restored draft; focusing a
  // textarea that already has text otherwise leaves the cursor at the start.
  useEffect(() => {
    const input = inputRef.current;
    if (input && input.value.length > 0) {
      const caretPosition = input.value.length;
      input.focus();
      input.setSelectionRange(caretPosition, caretPosition);
    }
  }, []);

  const checkMicrophonePermission = async (): Promise<"granted" | "denied" | "prompt"> => {
    // Try Permissions API first (works on desktop Chrome/Firefox)
    try {
      const result = await navigator.permissions.query({ name: "microphone" });
      return result.state as "granted" | "denied" | "prompt";
    } catch {
      // Permissions API not supported (mobile Safari, etc.)
      // Try getUserMedia to check permission
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((track) => track.stop());
        return "granted";
      } catch (err) {
        if (err instanceof Error && (err.name === "NotAllowedError" || err.name === "PermissionDeniedError")) {
          return "denied";
        }
        return "prompt";
      }
    }
  };

  const toggleRecording = async () => {
    if (isRecording) {
      recognitionRef?.current?.stop();
      setIsRecording(false);
      return;
    }

    try {
      const permissionState = await checkMicrophonePermission();

      if (permissionState === "denied") {
        setIsMicrophoneDenied(true);
        setIsPermissionsModal(true);
        return;
      }

      if (permissionState === "prompt") {
        setIsMicrophoneDenied(false);
        setIsPermissionsModal(true);
      }

      // eslint-disable-next-line @typescript-eslint/naming-convention -- Constructor reference requires PascalCase for new-cap rule compliance
      const SpeechRecognitionConstructor = window.SpeechRecognition || window.webkitSpeechRecognition;

      recognitionRef.current = new SpeechRecognitionConstructor();
      if (!recognitionRef.current) throw new Error("No speech recognition");
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = "en-US";

      recognitionRef.current.onresult = (event) => {
        if (event.results[event.results.length - 1].isFinal) {
          setMessage(event.results[event.results.length - 1][0].transcript?.slice(0, CHAT_MESSAGE_MAX_LENGTH) ?? "");
        }
      };

      recognitionRef.current.onspeechend = () => {
        setIsRecording(false);
      };

      recognitionRef.current.onerror = (event) => {
        if (event.error === "not-allowed") {
          setIsMicrophoneDenied(true);
          setIsPermissionsModal(true);
        } else {
          handleSpeechRecognitionError(event.error);
        }
        setIsRecording(false);
      };
      recognitionRef.current.start();
      setIsRecording(true);
    } catch (speechError) {
      logger.error("Error while starting speech recognition:", speechError);
    }
  };

  const sendMessage = async (message: string, imageKey?: string): Promise<unknown> => {
    if (isChatWithGroup && !isAutoResponseActivated) {
      // Group chat: store the message as pending instead of transmitting it.
      // Signal this explicitly so the caller keeps the stored draft (nothing was
      // actually sent), unlike a real successful send.
      onPendingMessage(message);
      return { queuedAsPending: true };
    }
    // Normal flow: send message immediately
    setCharacterThatIsTyping(character);
    if (!conversationId) {
      // First send in a brand-new chat: the response assigns the conversation id.
      refSentFromNewChat.current = true;
    }
    // generate resolves with the created message on success, or a failure
    // descriptor ({ sendFailed, errorCode }) that lets the caller distinguish a
    // coded failure from a generic one.
    return generate({ type: "text", content: message, image_key: imageKey });
  };

  const handleSendMessage = async () => {
    if (isImageUploading || isSubmittingRef.current) {
      return;
    }

    if (shouldShowLoginOverlay) {
      return dispatch(toggleOverlayLogin());
    }

    isSubmittingRef.current = true;

    try {
      const msg = messageToSend;
      const contentToRestore = msg;
      const capturedClearDraft = clearDraft;
      const capturedSaveDraft = saveDraft;
      const imageKey = attachedImageKeyToSend ?? undefined;
      const imageIdToRestore = imageId;
      const imageDataUrlToRestore = getAttachedImageDataUrl();

      // Optimistically clear the composer, but keep the stored draft until we know
      // the send outcome (guarded by refIsSending so the persist effect does not
      // remove it).
      refIsSending.current = true;
      setMessage("");
      setSearchCommandMessage("");
      setOocCommandMessage("");
      setIsSearchCommandBoxVisible(false);
      setIsNormalCommandBoxVisible(false);

      if (attachedImageKeyToSend) {
        // Clear the attached image immediately so it disappears as the message is sent.
        setAttachedImageKeyToSend(null);
        setUploaderResetSignal((signal) => signal + 1);
      }

      let sendOutcome: unknown;
      try {
        // Send with the image key (state won't update until next render, so attachedImageKeyToSend still has the original value here)
        sendOutcome = await sendMessage(msg, imageKey);
      } finally {
        refIsSending.current = false;
      }

      const wasQueuedAsPending =
        typeof sendOutcome === "object" && sendOutcome !== null && (sendOutcome as { queuedAsPending?: boolean }).queuedAsPending === true;

      const sendFailed = !sendOutcome || (typeof sendOutcome === "object" && (sendOutcome as { sendFailed?: boolean }).sendFailed === true);

      if (wasQueuedAsPending) {
        refSentFromNewChat.current = false;
        refPendingDraftText.current = contentToRestore;
        capturedSaveDraft(contentToRestore);
      } else if (!sendFailed) {
        // Clear the draft of the conversation that actually sent, not the one
        // currently shown, so a mid-flight chat switch cannot delete the wrong
        // draft or leave the sent chat's draft behind.
        capturedClearDraft();
      } else {
        refSentFromNewChat.current = false;
        const failureErrorCode =
          typeof sendOutcome === "object" && sendOutcome !== null ? ((sendOutcome as { errorCode?: string | null }).errorCode ?? null) : null;
        if (failureErrorCode) {
          // Coded failure: the optimistic user bubble is kept (with retry), so
          // restoring the composer would duplicate the message. Drop the draft
          // since the text is preserved in the bubble.
          capturedClearDraft();
        } else {
          // Generic failure: the optimistic bubble was rolled back, so restore
          // the composer text (and any attached image) for a retry.
          setMessage(contentToRestore);
          if (imageKey) {
            restoreImage({ imageKey, imageId: imageIdToRestore, dataUrl: imageDataUrlToRestore });
            setAttachedImageKeyToSend(imageKey);
          }
        }
      }

      if (isAutoPilotPressed) {
        setIsAutoPilotPressed(false);
      }
    } finally {
      isSubmittingRef.current = false;
    }
  };
  useEffect(() => {
    if (isDesktop) {
      inputRef?.current?.focus();
    }
  }, [typingMode, isCloneLoading]);

  useLayoutEffect(() => {
    scrollToBottom();
  }, [messageToSend]);

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Check if the key pressed is 'Enter' and the Shift key is not pressed
    if (
      messageHasSomeValue &&
      event.key === "Enter" &&
      !event.shiftKey &&
      !/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    ) {
      event.preventDefault(); // Prevent the default behavior (e.g., form submission or newline in textarea)

      if (event.repeat || isDisabled) return;

      void handleSendMessage();
    }
  };

  const onAutoPilotPressed = async () => {
    setIsInputActionDisabled(true);

    const autopilotResponse = (await generate({
      type: "text",
      autopilot: true,
      abortSignal: chatAbortController.signal,
    })) as string;

    if (autopilotResponse) {
      const truncatedAutopilot = autopilotResponse.slice(0, CHAT_MESSAGE_MAX_LENGTH);
      setMessage(truncatedAutopilot);
      setIsAutoPilotPressed(true);
    }
    chatAbortRef.current = new AbortController();
    setIsInputActionDisabled(false);
  };

  useEffect(() => {
    if (window.innerWidth > WIDE_SCREEN_WIDTH_THRESHOLD) {
      inputRef?.current?.focus();
    }
  }, [typingMode]);

  const onGenerateImage = async () => {
    if (isImageGenerationPaywall) {
      setOpenModalType(ModalType.PREMIUM_ACCESS);
      return;
    }
    if (!isImageGenerationAllowed) {
      return;
    }

    const isLegacyMode = (await typedLocalStorageService.getItem(CONVIMAGE_LEGACYMODE)) === "true";
    if (characterWithLora?.lora_status === "ready" || (permissions?.includes(SPICYCHAT_USER_PERMISSIONS.conversation_images_v2) && !isLegacyMode)) {
      setCharacterThatIsTyping(character);
      // If last message is an image then regenerating the image,
      // otherwise generating image normally
      await generate({ type: "image", regenerate: isLastMessageImage });
    } else if (permissions?.includes(SPICYCHAT_USER_PERMISSIONS.image_training)) {
      setOpenModalType(ModalType.TRAINING);
    } else {
      setOpenModalType(ModalType.PREMIUM_TRAINING);
    }
  };

  const handleChatInputActions = (action: string) => {
    switch (action) {
      case CHAT_INPUT_ACTIONS.SEND:
        return handleSendMessage();
      case CHAT_INPUT_ACTIONS.AUTO_PILOT:
        return onAutoPilotPressed();
      case CHAT_INPUT_ACTIONS.TOGGLE_RECORDING:
        return toggleRecording();
    }
  };

  const handleContentSizeChange = () => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    const scrollHeight = el.scrollHeight;

    if (!isMobile) {
      el.style.height = `${scrollHeight}px`;
      if (isActionMenuAtRight) {
        setIsActionMenuAtRight(false);
      }
      return;
    }

    const { lineHeight: lineHeightVal, paddingTop: paddingTopVal, paddingBottom: paddingBottomVal } = window.getComputedStyle(el);
    const lineHeight = parseFloat(lineHeightVal);
    const paddingTop = parseFloat(paddingTopVal);
    const paddingBottom = parseFloat(paddingBottomVal);
    const rowsThreshold = 3; // after 3 rows we need move action menu to the right
    const rowsThresholdHeight = rowsThreshold * lineHeight + paddingTop + paddingBottom;
    const heightBuffer = lineHeight / 2;
    const totalHeightThreshold = rowsThresholdHeight + heightBuffer;
    if (message.length === 0) {
      el.style.height = `${scrollHeight}px`;
      if (isActionMenuAtRight) {
        setIsActionMenuAtRight(false);
      }
      return;
    }

    if (scrollHeight > totalHeightThreshold) {
      if (!isActionMenuAtRight) {
        setIsActionMenuAtRight(true);
      }
      el.style.height = `${scrollHeight}px`;
      return;
    }

    el.style.height = `${scrollHeight}px`;
  };

  useLayoutEffect(() => {
    handleContentSizeChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- including isActionMenuAtRight re-runs layout on menu toggle and caused React #185 infinite loop (NX-10550). Menu state is only read when message or isMobile changes.
  }, [message, isMobile]);

  const listItems: DropDownMenuItem[] = (() => {
    const items = [
      {
        icon: IconVariants.GLOBE,
        label: t("chat:chatInput.menu.webSearch"),
        isDisabled,
        onPress: () => {
          setIsSearchCommandBoxVisible(true);
        },
      },
      {
        icon: "Command" as IconVariants,
        label: t("chat:chatInput.menu.command"),
        isDisabled,
        onPress: () => {
          setIsNormalCommandBoxVisible(true);
        },
      },
    ];
    if (shouldRenderImageUploader) {
      items.unshift({
        icon: "Camera" as IconVariants,
        label: t("chat:chatInput.menu.attachImage"),
        isDisabled,
        onPress: () => {
          if (!isAuthenticated) {
            dispatch(toggleOverlayLogin());
            return;
          }
          handleUploadClick();
        },
      });
    }

    if (messageHasSomeValue) {
      if (!hideSuggest) {
        items.unshift({
          icon: IconVariants.LIGHT,
          label: t("chat:chatInput.menu.suggestReply"),
          onPress: () => void handleChatInputActions(CHAT_INPUT_ACTIONS.AUTO_PILOT),
          isDisabled,
        });
      }

      if (!(isPartner || isChatWithGroup) && isImageGenerationVisible) {
        items.unshift({
          icon: IconVariants.IMAGE,
          label: t("chat:chatInput.menu.generateImage"),
          onPress: () => void onGenerateImage(),
          isDisabled,
        });
      }
    }

    return items;
  })();

  if (isMobile && isMessageEditMode) {
    return null;
  }

  return (
    <Row className="bg-transparent w-full right-0 pb-md z-[1] flex justify-center m-auto" style={{ maxWidth: CHAT_PAGE_WIDTH }}>
      <div className="w-full">
        <div ref={wrapperRef} className="flex items-end gap-sm w-full">
          <div className="h-full flex items-end gap-sm mb-1">
            {!isActionMenuAtRight && (
              <InputActionMenu
                menuItems={listItems}
                disabled={isInputActionDisabled}
                loading={isInputActionDisabled ? !isGroupBelowMinimum : false}
              />
            )}
            {!(isImageIconHidden || isChatWithGroup) && (
              <Tooltip title={t("chat:page.input.tooltip.image")} offset={0}>
                <ButtonIcon
                  data-testid="ImageGenerationButton"
                  icon={IconVariants.IMAGE}
                  size={ButtonSize.MEDIUM}
                  variant={ButtonVariant.TRANSPARENT}
                  disabled={isDisabled}
                  onClick={() => void onGenerateImage()}
                  aria-label="generate-image"
                />
              </Tooltip>
            )}
            {!messageHasSomeValue && !isInputActionDisabled && !hideSuggest && (
              <Tooltip title={t("chat:page.input.tooltip.suggest")} offset={0} visible={!isInputActionDisabled}>
                <ButtonIcon
                  icon={IconVariants.LIGHT}
                  size={ButtonSize.MEDIUM}
                  variant={!isInputActionDisabled ? ButtonVariant.TRANSPARENT : ButtonVariant.SECONDARY}
                  onClick={() => void handleChatInputActions(CHAT_INPUT_ACTIONS.AUTO_PILOT)}
                  loading={isInputActionDisabled ? !hasPendingMessage : undefined}
                  loaderSize={SpinnerSize.SMALL}
                  disabled={isDisabled}
                  aria-label="autogenerate"
                />
              </Tooltip>
            )}
          </div>
          <div className="w-full flex">
            <div className="grow border-1 border-solid border-gray-5 rounded-[13px] py-sm px-2 bg-gray-3 transition-colors focus-within:border-blue-10">
              {shouldRenderImageUploader ? (
                <ImageUploadPreview
                  imageFileKey={displayImageKey ?? null}
                  isImageCleared={isImageCleared}
                  isUploading={isUploading}
                  onClearImage={handleClearImage}
                  isImageModalOpen={isImageModalOpen}
                  onImageModalOpenChange={setIsImageModalOpen}
                />
              ) : null}
              <CommandInputBox
                isDisabled={isDisabled}
                label="/search"
                isVisible={isSearchCommandBoxVisible}
                onClose={() => setIsSearchCommandBoxVisible(false)}
                value={searchCommandMessage}
                onValueChange={setSearchCommandMessage}
              />
              <CommandInputBox
                isDisabled={isDisabled}
                label="/cmd"
                isVisible={isNormalCommandBoxVisible}
                onClose={() => setIsNormalCommandBoxVisible(false)}
                value={oocCommandMessage}
                onValueChange={setOocCommandMessage}
              />

              <div className="flex flex-1 items-end gap-1">
                <textarea
                  className={cn(
                    "w-full flex-grow max-h-[188px] py-0.5 resize-none text-paragraph-md bg-transparent focus:outline-none",
                    isSearchCommandBoxVisible && isNormalCommandBoxVisible && "max-h-[124px]",
                  )}
                  ref={inputRef}
                  value={message}
                  onFocus={() => onInputFocusChange?.(true)}
                  onBlur={() => onInputFocusChange?.(false)}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    setMessage(e.target.value.slice(0, CHAT_MESSAGE_MAX_LENGTH));
                  }}
                  placeholder={!isRecording ? t("chat:chatInput.placeholder.message") : t("chat:chatInput.placeholder.recording")}
                  disabled={isDisabled}
                  onKeyDown={(event) => void handleKeyDown(event)}
                  rows={1}
                  maxLength={CHAT_MESSAGE_MAX_LENGTH}
                />
              </div>
            </div>
          </div>
          <div className="mb-1">
            {isInputActionDisabled && !hasPendingMessage ? (
              <ButtonIcon
                classNames={{ icon: "fill-background !size-[14px]" }}
                icon={IconVariants.SQUARE}
                onClick={() => {
                  chatAbortController?.abort();
                }}
                size={ButtonSize.MEDIUM}
                variant={ButtonVariant.INVERSE}
                aria-label="cancel-generation"
              />
            ) : null}
            <Col className="gap-sm">
              {isActionMenuAtRight ? (
                <InputActionMenu
                  menuItems={listItems}
                  disabled={isInputActionDisabled}
                  loading={isInputActionDisabled ? !isGroupBelowMinimum : false}
                  placement="top-end"
                />
              ) : null}
              {messageHasSomeValue && !isInputActionDisabled ? (
                <ButtonIcon
                  icon={IconVariants.ARROW_UP}
                  size={ButtonSize.MEDIUM}
                  variant={ButtonVariant.INVERSE}
                  onClick={() => void handleChatInputActions(CHAT_INPUT_ACTIONS.SEND)}
                  disabled={isDisabled || isImageUploading}
                  aria-label="send-message"
                />
              ) : null}
            </Col>

            {!messageHasSomeValue && !isInputActionDisabled && (
              <ButtonIcon
                icon={!isRecording ? IconVariants.MIC : IconVariants.CHECK}
                size={ButtonSize.MEDIUM}
                variant={ButtonVariant.SECONDARY}
                onClick={() => void handleChatInputActions(CHAT_INPUT_ACTIONS.TOGGLE_RECORDING)}
                disabled={isDisabled}
                aria-label="record-voice"
              />
            )}
          </div>
        </div>
        {shouldRenderImageUploader ? (
          <input ref={fileInputRef} id={IMAGE_INPUT_ID} type="file" accept={ACCEPTED_FILE_TYPES.join(",")} hidden />
        ) : null}
        <ModalPremiumAccess
          isOpen={openModalType === "PremiumAccess"}
          onClose={() => setOpenModalType(null)}
          characterAvatarUrl={character.avatar_url as string}
          isCharacterAvatarBlurred={isCharacterAvatarBlurred}
          paywallRoles={imageGenerationCapability?.paywallRoles}
        />
        <ModalPremiumTraining isOpen={openModalType === "PremiumTraining"} onClose={() => setOpenModalType(null)} />

        {openModalType === "Training" && (
          <ModalTraining isOpen={openModalType === "Training"} onClose={() => setOpenModalType(null)} character={character} />
        )}

        <PermissionsModal
          isOpen={isPermissionsModal}
          setIsPermissionsModal={setIsPermissionsModal}
          permission="microphone"
          isDenied={isMicrophoneDenied}
        />
      </div>
    </Row>
  );
}

export default ChatInput;
